import React, { useState, useEffect, useCallback } from 'react';
import { format } from 'date-fns';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { useCreateEvent, useUpdateEvent } from '../../hooks/useEvents';
import { toast } from 'sonner';
import { EVENT_TYPE_CONFIG } from './eventConstants';

const EVENT_TYPES = Object.entries(EVENT_TYPE_CONFIG).map(([value, cfg]) => ({
  value,
  label: cfg.label,
}));

function toLocalDatetime(date) {
  if (!date) return '';
  const d = new Date(date);
  // Format as YYYY-MM-DDTHH:MM for datetime-local input
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function toLocalDate(date) {
  if (!date) return '';
  const d = new Date(date);
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

const INITIAL_STATE = {
  title: '',
  description: '',
  event_type: 'shoot_day',
  start_at: '',
  end_at: '',
  all_day: false,
  location_text: '',
  cost_amount: '',
  cost_currency: 'INR',
};

export default function EventFormDialog({ open, onClose, filmId, event, defaultDate }) {
  const createMutation = useCreateEvent();
  const updateMutation = useUpdateEvent();
  const isEditing = !!event;

  const [form, setForm] = useState(INITIAL_STATE);
  const [errors, setErrors] = useState({});

  // Populate form on open/edit
  useEffect(() => {
    if (!open) return;
    if (event) {
      setForm({
        title: event.title || '',
        description: event.description || '',
        event_type: event.event_type || 'shoot_day',
        start_at: event.all_day
          ? toLocalDate(event.start_at)
          : toLocalDatetime(event.start_at),
        end_at: event.all_day
          ? toLocalDate(event.end_at)
          : toLocalDatetime(event.end_at),
        all_day: event.all_day || false,
        location_text: event.location_text || '',
        cost_amount: event.cost_amount != null ? String(event.cost_amount) : '',
        cost_currency: event.cost_currency || 'INR',
      });
    } else {
      // New event
      const base = defaultDate || new Date();
      const startStr = toLocalDatetime(new Date(base.getFullYear(), base.getMonth(), base.getDate(), 9, 0));
      const endStr = toLocalDatetime(new Date(base.getFullYear(), base.getMonth(), base.getDate(), 10, 0));
      setForm({ ...INITIAL_STATE, start_at: startStr, end_at: endStr });
    }
    setErrors({});
  }, [open, event, defaultDate]);

  const handleChange = useCallback((field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: undefined }));
  }, []);

  const handleAllDayToggle = useCallback((checked) => {
    setForm((prev) => {
      const updated = { ...prev, all_day: checked };
      if (checked) {
        // Convert datetime to date
        updated.start_at = toLocalDate(prev.start_at || new Date());
        updated.end_at = toLocalDate(prev.end_at || new Date());
      } else {
        // Convert date to datetime (add default times)
        const startBase = prev.start_at ? new Date(prev.start_at + 'T09:00') : new Date();
        const endBase = prev.end_at ? new Date(prev.end_at + 'T10:00') : new Date();
        updated.start_at = toLocalDatetime(startBase);
        updated.end_at = toLocalDatetime(endBase);
      }
      return updated;
    });
  }, []);

  const validate = useCallback(() => {
    const errs = {};
    if (!form.title.trim()) errs.title = 'Title is required';
    if (!form.start_at) errs.start_at = 'Start date/time is required';
    if (!form.end_at) errs.end_at = 'End date/time is required';
    if (form.start_at && form.end_at) {
      const s = new Date(form.start_at);
      const e = new Date(form.end_at);
      if (e < s) errs.end_at = 'End must be after start';
    }
    if (form.cost_amount && (isNaN(Number(form.cost_amount)) || Number(form.cost_amount) < 0)) {
      errs.cost_amount = 'Must be a positive number';
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  }, [form]);

  const handleSubmit = async () => {
    if (!validate()) return;

    const startISO = new Date(form.all_day ? form.start_at + 'T00:00:00' : form.start_at).toISOString();
    const endISO = new Date(form.all_day ? form.end_at + 'T23:59:59' : form.end_at).toISOString();

    const payload = {
      title: form.title.trim(),
      description: form.description.trim() || null,
      event_type: form.event_type,
      start_at: startISO,
      end_at: endISO,
      all_day: form.all_day,
      location_text: form.location_text.trim() || null,
      cost_amount: form.cost_amount ? Number(form.cost_amount) : null,
      cost_currency: form.cost_currency || 'INR',
    };

    try {
      if (isEditing) {
        await updateMutation.mutateAsync({ id: event.id, ...payload });
        toast.success('Event updated');
      } else {
        await createMutation.mutateAsync({ ...payload, film_id: filmId });
        toast.success('Event created');
      }
      onClose();
    } catch (err) {
      const detail = err.response?.data?.detail || err.message;
      toast.error(`Failed to ${isEditing ? 'update' : 'create'} event: ${detail}`);
    }
  };

  const isPending = createMutation.isPending || updateMutation.isPending;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">
            {isEditing ? 'Edit Event' : 'New Event'}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Title */}
          <div className="space-y-1.5">
            <Label htmlFor="evt-title">Title *</Label>
            <Input
              id="evt-title"
              placeholder="e.g. Shoot Day 1 — Office Scene"
              value={form.title}
              onChange={(e) => handleChange('title', e.target.value)}
              className={errors.title ? 'border-destructive' : ''}
            />
            {errors.title && <p className="text-xs text-destructive">{errors.title}</p>}
          </div>

          {/* Event type */}
          <div className="space-y-1.5">
            <Label>Event Type</Label>
            <Select value={form.event_type} onValueChange={(v) => handleChange('event_type', v)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {EVENT_TYPES.map((t) => (
                  <SelectItem key={t.value} value={t.value}>
                    <span className="flex items-center gap-2">
                      <span className={`h-2 w-2 rounded-full ${EVENT_TYPE_CONFIG[t.value].dot}`} />
                      {t.label}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* All day toggle */}
          <div className="flex items-center gap-3">
            <Switch
              checked={form.all_day}
              onCheckedChange={handleAllDayToggle}
              id="evt-allday"
            />
            <Label htmlFor="evt-allday" className="text-sm cursor-pointer">All Day</Label>
          </div>

          {/* Date / time */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="evt-start">Start *</Label>
              <Input
                id="evt-start"
                type={form.all_day ? 'date' : 'datetime-local'}
                value={form.start_at}
                onChange={(e) => handleChange('start_at', e.target.value)}
                className={errors.start_at ? 'border-destructive' : ''}
              />
              {errors.start_at && <p className="text-xs text-destructive">{errors.start_at}</p>}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="evt-end">End *</Label>
              <Input
                id="evt-end"
                type={form.all_day ? 'date' : 'datetime-local'}
                value={form.end_at}
                onChange={(e) => handleChange('end_at', e.target.value)}
                className={errors.end_at ? 'border-destructive' : ''}
              />
              {errors.end_at && <p className="text-xs text-destructive">{errors.end_at}</p>}
            </div>
          </div>

          {/* Description */}
          <div className="space-y-1.5">
            <Label htmlFor="evt-desc">Description</Label>
            <Textarea
              id="evt-desc"
              placeholder="Add notes, call sheets, or details…"
              rows={3}
              value={form.description}
              onChange={(e) => handleChange('description', e.target.value)}
            />
          </div>

          {/* Location */}
          <div className="space-y-1.5">
            <Label htmlFor="evt-loc">Location</Label>
            <Input
              id="evt-loc"
              placeholder="e.g. Studio A, Zoom link"
              value={form.location_text}
              onChange={(e) => handleChange('location_text', e.target.value)}
            />
          </div>

          {/* Cost */}
          <div className="grid grid-cols-3 gap-3">
            <div className="col-span-2 space-y-1.5">
              <Label htmlFor="evt-cost">Cost</Label>
              <Input
                id="evt-cost"
                type="number"
                min="0"
                step="0.01"
                placeholder="0.00"
                value={form.cost_amount}
                onChange={(e) => handleChange('cost_amount', e.target.value)}
                className={errors.cost_amount ? 'border-destructive' : ''}
              />
              {errors.cost_amount && <p className="text-xs text-destructive">{errors.cost_amount}</p>}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="evt-currency">Currency</Label>
              <Select value={form.cost_currency} onValueChange={(v) => handleChange('cost_currency', v)}>
                <SelectTrigger id="evt-currency">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="INR">INR</SelectItem>
                  <SelectItem value="USD">USD</SelectItem>
                  <SelectItem value="EUR">EUR</SelectItem>
                  <SelectItem value="GBP">GBP</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isPending}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={isPending}>
            {isPending
              ? (isEditing ? 'Saving…' : 'Creating…')
              : (isEditing ? 'Save Changes' : 'Create Event')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
